import React, { useMemo } from 'react';
import { FiSearch, FiChevronLeft, FiChevronRight, FiInbox } from 'react-icons/fi';

/**
 * The single table component for the app.
 *
 * It renders only — data, paging and actions are owned by the caller (pair it
 * with `useTableData` / `useClientTable` for the fetching half).
 *
 * @param {Array}    headers    ['Name', ...] or [{ key, label, render, align, width, className }]
 * @param {Array}    data       array of row objects, or array of cell arrays
 * @param {function} actions    (row, index) => node, rendered in a pinned right-hand column
 * @param {string}   rowKey     field to key rows by (default `_id`, then `id`, then index)
 * @param {function} onRowClick (row, index) => void
 * @param {number}   total      total record count, when known, for "showing x–y of z"
 */

const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' };

const normalizeColumns = (headers = []) =>
  headers.map((header, index) => {
    if (header && typeof header === 'object') {
      return {
        key: header.key ?? index,
        label: header.label ?? '',
        render: header.render,
        align: ALIGN[header.align] || ALIGN.left,
        width: header.width,
        className: header.className || '',
      };
    }
    return { key: header, label: header, align: ALIGN.left, className: '' };
  });

const getRowKey = (row, index, rowKey) => {
  if (rowKey && row?.[rowKey] != null) return row[rowKey];
  return row?._id ?? row?.id ?? index;
};

const pageWindow = (page, totalPages) => {
  const span = 5;
  let start = Math.max(1, page - Math.floor(span / 2));
  const end = Math.min(totalPages, start + span - 1);
  start = Math.max(1, end - span + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

export default function DataTable({
  headers = [],
  data = [],
  title = null,
  subtitle = null,
  actionButton = null,
  toolbar = null,
  actions = null,
  actionsLabel = 'Actions',
  rowKey,
  onRowClick,
  page = 1,
  totalPages = 1,
  rowsPerPage = 10,
  total = null,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 100],
  onSearch,
  searchValue,
  searchPlaceholder = 'Search records...',
  filters = [],
  onFilter,
  isLoading = false,
  emptyMessage = 'No records found.',
  emptyAction = null,
  dense = false,
  stickyActions = true,
  className = '',
}) {
  const columns = useMemo(() => normalizeColumns(headers), [headers]);
  const hasActions = typeof actions === 'function';
  const colCount = Math.max(columns.length + (hasActions ? 1 : 0), 1);
  const cellPad = dense ? 'px-4 py-2' : 'px-5 py-3.5';
  const searchable = typeof onSearch === 'function';
  const showToolbar = Boolean(title || subtitle || actionButton || toolbar || searchable || filters?.length);
  const showFooter = Boolean(onPageChange || onRowsPerPageChange);

  const firstRow = data.length ? (page - 1) * rowsPerPage + 1 : 0;
  const lastRow = data.length ? firstRow + data.length - 1 : 0;

  const stickyCell = stickyActions
    ? 'sticky right-0 z-[5] bg-white group-hover:bg-sky-50/60 shadow-[-8px_0_12px_-10px_rgba(15,23,42,0.18)]'
    : '';

  return (
    <div
      className={`w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_32px_-18px_rgba(15,23,42,0.25)] ${className}`}
    >
      {showToolbar && (
        <div className="flex flex-col gap-4 border-b border-slate-100 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
          {(title || subtitle) && (
            <div className="min-w-0">
              {title && <h2 className="truncate text-base font-semibold tracking-tight text-slate-900">{title}</h2>}
              {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2.5 lg:justify-end">
            {searchable && (
              <div className="relative min-w-0 flex-1 sm:min-w-[240px] sm:flex-none">
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchValue}
                  placeholder={searchPlaceholder}
                  onChange={(e) => onSearch(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:bg-white focus:ring-4 focus:ring-sky-500/10"
                />
              </div>
            )}

            {filters?.map((filter) => (
              <select
                key={filter.key}
                value={filter.value}
                onChange={(e) => onFilter && onFilter(filter.key, e.target.value)}
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-sky-500 focus:bg-white focus:ring-4 focus:ring-sky-500/10"
              >
                <option value="">{filter.label || 'All'}</option>
                {filter.options?.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ))}

            {toolbar}
            {actionButton}
          </div>
        </div>
      )}

      <div className="max-h-[70vh] overflow-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  style={col.width ? { width: col.width } : undefined}
                  className={`${cellPad} sticky top-0 z-10 whitespace-nowrap bg-slate-50 font-semibold ${col.align} ${col.className}`}
                >
                  {col.label}
                </th>
              ))}
              {hasActions && (
                <th
                  className={`${cellPad} sticky top-0 whitespace-nowrap bg-slate-50 text-right font-semibold ${
                    stickyActions ? 'right-0 z-20 shadow-[-8px_0_12px_-10px_rgba(15,23,42,0.18)]' : 'z-10'
                  }`}
                >
                  {actionsLabel}
                </th>
              )}
            </tr>
          </thead>

          <tbody className="text-slate-700">
            {isLoading ? (
              Array.from({ length: Math.min(rowsPerPage || 5, 6) }).map((_, rowIdx) => (
                <tr key={`skeleton-${rowIdx}`} className="border-b border-slate-100 last:border-0">
                  {Array.from({ length: colCount }).map((__, cellIdx) => (
                    <td key={cellIdx} className={cellPad}>
                      <div className="h-3 animate-pulse rounded-full bg-slate-200/80" style={{ width: `${45 + ((cellIdx * 17 + rowIdx * 11) % 45)}%` }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : !data?.length ? (
              <tr>
                <td colSpan={colCount} className="px-5 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
                      <FiInbox className="h-6 w-6 text-slate-400" />
                    </div>
                    <p className="text-sm font-medium text-slate-500">{emptyMessage}</p>
                    {emptyAction}
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, rowIdx) => (
                <tr
                  key={getRowKey(row, rowIdx, rowKey)}
                  onClick={onRowClick ? () => onRowClick(row, rowIdx) : undefined}
                  className={`group border-b border-slate-100 transition-colors last:border-0 hover:bg-sky-50/60 ${
                    onRowClick ? 'cursor-pointer' : ''
                  }`}
                >
                  {Array.isArray(row)
                    ? row.map((cell, cellIdx) => (
                        <td key={cellIdx} className={`${cellPad} whitespace-nowrap ${columns[cellIdx]?.align || ALIGN.left}`}>
                          {cell}
                        </td>
                      ))
                    : columns.map((col, colIdx) => {
                        const value = row?.[col.key];
                        const rendered = col.render ? col.render(value, row, rowIdx) : value;
                        const isEmpty = rendered == null || rendered === '';
                        return (
                          <td key={colIdx} className={`${cellPad} whitespace-nowrap ${col.align} ${col.className}`}>
                            {isEmpty ? <span className="text-slate-300">—</span> : rendered}
                          </td>
                        );
                      })}

                  {hasActions && (
                    <td className={`${cellPad} whitespace-nowrap ${stickyCell}`} onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">{actions(row, rowIdx)}</div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showFooter && (
        <div className="flex flex-col gap-3 border-t border-slate-100 px-5 py-3.5 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            {onRowsPerPageChange && (
              <label className="flex items-center gap-2">
                <span className="hidden sm:inline">Rows per page</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700 outline-none transition focus:border-sky-500 focus:bg-white"
                >
                  {rowsPerPageOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {!isLoading && data.length > 0 && (
              <span className="tabular-nums">
                {firstRow}–{lastRow}
                {total != null ? ` of ${total}` : ''}
              </span>
            )}
          </div>

          {onPageChange && (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={page <= 1 || isLoading}
                onClick={() => onPageChange(page - 1)}
                aria-label="Previous page"
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <FiChevronLeft className="h-4 w-4" />
              </button>

              {pageWindow(page, Math.max(totalPages, 1)).map((num) => (
                <button
                  key={num}
                  type="button"
                  disabled={isLoading}
                  onClick={() => onPageChange(num)}
                  className={`h-8 min-w-[2rem] rounded-lg px-2 text-xs font-medium tabular-nums transition ${
                    num === page
                      ? 'bg-sky-600 text-white shadow-sm shadow-sky-600/30'
                      : 'border border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {num}
                </button>
              ))}

              <button
                type="button"
                disabled={page >= totalPages || isLoading}
                onClick={() => onPageChange(page + 1)}
                aria-label="Next page"
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <FiChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
